import { z } from "zod";

/** Row shape aligned with API / Postgres — timestamps as ISO strings from JSON. */

export type Business = {
  id: string;
  name: string;
  slug: string;
  category: string;
  timezone: string;
  currency: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  accentColor: string | null;
  minLeadTimeMins: number | null;
  maxBookingDays: number | null;
  slotIncrementMins: number | null;
  cancellationPolicyText: string | null;
  depositEnabled: boolean | null;
  defaultDepositType: string | null;
  defaultDepositValue: number | null;
  bookingPageConfig: unknown | null;
  bookingPageDraftConfig: unknown | null;
  brandingUpdatedAt: string | null;
};

export type InsertBusiness = Omit<Business, "id">;

export type Staff = {
  id: string;
  businessId: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  role: string | null;
  bio: string | null;
  active: boolean | null;
  /** Epos Now staff id when linked; used for directory pickers and API payloads. */
  eposNowId?: number | null;
  /** From GET `/admin/staff` (`StaffResponse.workingHours` in swagger); not set when omitted by API. */
  workingHours?: WorkingHours[];
};

export type InsertStaff = Omit<Staff, "id">;

export type Service = {
  id: string;
  businessId: string;
  name: string;
  description: string | null;
  category: string | null;
  durationMins: number;
  bufferBeforeMins: number | null;
  bufferAfterMins: number | null;
  priceCents: number;
  currency: string | null;
  depositEnabled: boolean | null;
  depositType: string | null;
  depositValue: number | null;
  depositPolicyText: string | null;
  active: boolean | null;
  /** From GET /admin/services; omitted or empty means all active staff are eligible (per API). */
  staffIds?: string[] | null;
  /** Epos Now product id when linked; used for “add offering” directory picker (POST body). */
  eposNowProductId?: number | null;
};

export type InsertService = Omit<Service, "id">;

export type WorkingHours = {
  id: string;
  staffId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean | null;
};

export type InsertWorkingHours = Omit<WorkingHours, "id">;

export type TimeOff = {
  id: string;
  staffId: string;
  startAt: string;
  endAt: string;
  reason: string | null;
};

export type InsertTimeOff = Omit<TimeOff, "id">;

export type Customer = {
  id: string;
  businessId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  notes: string | null;
  createdAt: string | null;
};

export type InsertCustomer = Omit<Customer, "id" | "createdAt">;

export type Booking = {
  id: string;
  businessId: string;
  serviceId: string;
  /** API may return null when unassigned */
  staffId: string | null;
  customerId: string;
  startAt: string;
  endAt: string;
  status: string;
  notes: string | null;
  manageToken: string;
  priceCents: number | null;
  currency: string | null;
  stripeSessionId: string | null;
  stripePaymentIntentId: string | null;
  depositAmountCents: number | null;
  paymentStatus: string | null;
  source: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type InsertBooking = Omit<Booking, "id" | "createdAt" | "updatedAt">;

export type BookingWithDetails = Booking & {
  service: Service;
  staffMember: Staff | null;
  customer: Customer;
  partySize?: number;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
};

/** Bookable resource (tables, rooms, etc.) — API may expose without a local table in this repo. */
export type Resource = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  capacity: number;
  active: boolean;
  /** Offerings linked to this resource (GET `/admin/resources` or resource detail). */
  serviceIds?: string[] | null;
};

export const bookingStatusEnum = ["pending", "pending_payment", "confirmed", "cancelled", "no_show", "completed"] as const;
export type BookingStatus = (typeof bookingStatusEnum)[number];

export type User = {
  id: string;
  username: string;
  password: string;
};

export type InsertUser = Pick<User, "username" | "password">;

export type ThemeMode = "light" | "dark" | "auto";
export type RadiusSize = "sm" | "md" | "lg" | "xl" | "pill";

export type BookingPageConfig = {
  pageTitle: string;
  tagline?: string;
  themeMode: ThemeMode;
  primaryColor: string;
  radius: RadiusSize;
  logoUrl?: string;
  coverImageUrl?: string;
  galleryUrls?: string[];
  showPrices: boolean;
  showDurations: boolean;
  showStaff: boolean;
  showAddress: boolean;
  showPhone: boolean;
  poweredByEposNow: boolean;
};

export const bookingPageConfigSchema = z.object({
  pageTitle: z.string().optional(),
  tagline: z.string().optional(),
  themeMode: z.enum(["light", "dark", "auto"]).optional(),
  primaryColor: z.string().optional(),
  radius: z.enum(["sm", "md", "lg", "xl", "pill"]).optional(),
  logoUrl: z.string().optional(),
  coverImageUrl: z.string().optional(),
  galleryUrls: z.array(z.string()).max(6).optional(),
  showPrices: z.boolean().optional(),
  showDurations: z.boolean().optional(),
  showStaff: z.boolean().optional(),
  showAddress: z.boolean().optional(),
  showPhone: z.boolean().optional(),
  poweredByEposNow: z.boolean().optional(),
});

export function defaultBookingPageConfig(merchantName: string): BookingPageConfig {
  return {
    pageTitle: `Book with ${merchantName}`,
    tagline: "",
    themeMode: "light",
    primaryColor: "#0EA5E9",
    radius: "xl",
    logoUrl: "",
    coverImageUrl: "",
    galleryUrls: [],
    showPrices: true,
    showDurations: true,
    showStaff: true,
    showAddress: true,
    showPhone: true,
    poweredByEposNow: true,
  };
}

export function sanitizeBookingPageConfig(input: unknown, merchantName: string): BookingPageConfig {
  const base = defaultBookingPageConfig(merchantName);
  const cfg = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const themeMode: ThemeMode =
    cfg.themeMode === "dark" || cfg.themeMode === "auto" ? cfg.themeMode : "light";
  const radius: RadiusSize = ["sm", "md", "lg", "xl", "pill"].includes(cfg.radius as string)
    ? (cfg.radius as RadiusSize)
    : base.radius;
  const gallery = Array.isArray(cfg.galleryUrls)
    ? cfg.galleryUrls.filter((u): u is string => typeof u === "string").slice(0, 6)
    : [];

  return {
    pageTitle:
      typeof cfg.pageTitle === "string" && cfg.pageTitle.trim() ? cfg.pageTitle.trim() : base.pageTitle,
    tagline: typeof cfg.tagline === "string" ? cfg.tagline : base.tagline,
    themeMode,
    primaryColor:
      typeof cfg.primaryColor === "string" && cfg.primaryColor.trim() ? cfg.primaryColor : base.primaryColor,
    radius,
    logoUrl: typeof cfg.logoUrl === "string" ? cfg.logoUrl : base.logoUrl,
    coverImageUrl: typeof cfg.coverImageUrl === "string" ? cfg.coverImageUrl : base.coverImageUrl,
    galleryUrls: gallery,
    showPrices: typeof cfg.showPrices === "boolean" ? cfg.showPrices : base.showPrices,
    showDurations: typeof cfg.showDurations === "boolean" ? cfg.showDurations : base.showDurations,
    showStaff: typeof cfg.showStaff === "boolean" ? cfg.showStaff : base.showStaff,
    showAddress: typeof cfg.showAddress === "boolean" ? cfg.showAddress : base.showAddress,
    showPhone: typeof cfg.showPhone === "boolean" ? cfg.showPhone : base.showPhone,
    poweredByEposNow: typeof cfg.poweredByEposNow === "boolean" ? cfg.poweredByEposNow : base.poweredByEposNow,
  };
}

export function radiusToCss(radius: RadiusSize): string {
  switch (radius) {
    case "sm":
      return "8px";
    case "md":
      return "12px";
    case "lg":
      return "16px";
    case "xl":
      return "20px";
    case "pill":
      return "999px";
    default:
      return "20px";
  }
}
